<template>
  <!-- Ввод кода -->
  <div v-if="view === 'input'" class="flex flex-col items-center w-full">
    <h2 class="text-[28px] max-md:text-[24px] font-bold text-[#3a3d44] text-center leading-[1.3] pb-3">
      Подтвердите почту ({{ formattedTime }})
    </h2>
    <p class="text-base text-[rgba(58,61,68,0.6)] text-center pb-6">
      Отправили код подтверждения на
      <span class="text-primary">{{ email }}</span>
    </p>
    <UINewPinCode ref="pinCodeRef" @sendConfirmCode="handleConfirmCode" />
    <p v-if="errorMessage" class="text-[#f8465b] text-base mt-4">{{ errorMessage }}</p>
    <p class="text-[#3a3d44] text-base mt-4">Не пришел код?</p>
    <p
      class="text-base mt-1"
      :class="resendCooldown > 0 ? 'text-[rgba(58,61,68,0.6)]' : 'text-primary cursor-pointer'"
      @click="resendCode"
    >
      {{ resendCooldown > 0 ? `Отправить код повторно (через ${resendCooldown} сек.)` : 'Отправить код повторно' }}
    </p>
  </div>

  <!-- Срок действия кода истек -->
  <div v-else-if="view === 'expired'" class="flex flex-col items-center w-full">
    <h2 class="text-[28px] max-md:text-[24px] font-bold text-[#3a3d44] text-center leading-[1.3] pb-3">
      Срок действия кода истек
    </h2>
    <p class="text-base text-[rgba(58,61,68,0.6)] text-center pb-6">
      Отправьте новый код на свою почту
    </p>
    <button
      class="bg-primary rounded-[12px] py-3 w-[412px] max-md:w-full text-white font-medium text-base"
      @click="resendCode"
    >
      Отправить код повторно
    </button>
    <p class="text-[#3a3d44] text-base mt-6 text-center">Проблема с подверждением?</p>
    <a
      :href="supportUrl"
      target="_blank"
      class="text-primary text-base mt-1 cursor-pointer text-center"
    >
      Связаться с тех.поддержкой
    </a>
  </div>

  <!-- Попытки исчерпаны -->
  <div v-else-if="view === 'exhausted'" class="flex flex-col items-center w-full">
    <h2 class="text-[28px] max-md:text-[24px] font-bold text-[#3a3d44] text-center leading-[1.3] pb-3">
      Попытки исчерпаны
    </h2>
    <p class="text-base text-[rgba(58,61,68,0.6)] text-center pb-6">
      Пожалуйста, свяжитесь с технической поддержкой
    </p>
    <a
      :href="supportUrl"
      target="_blank"
      class="flex items-center justify-center gap-2 bg-primary rounded-[12px] py-3 w-[412px] max-md:w-full text-white font-medium text-base"
    >
      <Icon name="fluent:person-support-28-filled" class="text-[32px] text-white" />
      Связаться с тех. поддержкой
    </a>
    <p class="text-[#f8465b] text-base mt-4">Вы исчерпали все попытки</p>
  </div>
</template>

<script setup lang="ts">
import { LoginMode } from '~/interfaces/login/UI';
import AuthService from '~/services/AuthService';
import { useUserStore } from '~/store/UserModule';
import { AxiosError } from 'axios';

const emit = defineEmits<{
  (e: 'setMode', mode: LoginMode): void;
}>();

const props = defineProps<{
  email: string;
  registrationId: number;
  expiredAt: string;
  password: string;
}>();

type ConfirmView = 'input' | 'expired' | 'exhausted';

const view = ref<ConfirmView>('input');
const errorMessage = ref('');
const remainingSeconds = ref(0);
const resendCooldown = ref(60);
const pinCodeRef = ref<{ clearPinCode: () => void } | null>(null);
const loading = ref(false);
const currentExpiredAt = ref(props.expiredAt);
const currentRegistrationId = ref(props.registrationId);

const supportUrl = computed(() => {
  return useRuntimeConfig().public.SUPPORT_BOT as string || 'https://t.me/suptelepost';
});

const formattedTime = computed(() => {
  const mins = Math.floor(remainingSeconds.value / 60);
  const secs = remainingSeconds.value % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
});

// Таймер обратного отсчёта срока действия кода
let expiryInterval: ReturnType<typeof setInterval> | null = null;

const startExpiryTimer = () => {
  if (expiryInterval) clearInterval(expiryInterval);

  const updateRemaining = () => {
    const utcDate = currentExpiredAt.value.endsWith('Z') ? currentExpiredAt.value : currentExpiredAt.value.replace(' ', 'T') + 'Z';
    const diff = Math.floor((new Date(utcDate).getTime() - Date.now()) / 1000);
    remainingSeconds.value = Math.max(0, diff);
    if (remainingSeconds.value <= 0) {
      if (expiryInterval) clearInterval(expiryInterval);
      view.value = 'expired';
    }
  };

  updateRemaining();
  expiryInterval = setInterval(updateRemaining, 1000);
};

// Таймер cooldown для повторной отправки
let resendInterval: ReturnType<typeof setInterval> | null = null;

const startResendCooldown = () => {
  resendCooldown.value = 60;
  if (resendInterval) clearInterval(resendInterval);
  resendInterval = setInterval(() => {
    resendCooldown.value--;
    if (resendCooldown.value <= 0) {
      if (resendInterval) clearInterval(resendInterval);
    }
  }, 1000);
};

const handleConfirmCode = async (code: string) => {
  if (loading.value) return;
  loading.value = true;
  errorMessage.value = '';

  try {
    await AuthService.confirmRegistration(code);
    // Успех — авторизуем пользователя
    const userStore = useUserStore();
    await userStore.emailLogin(props.email, props.password);
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string; context?: Record<string, unknown> }>;
    const status = axiosError?.response?.status;
    const responseData = axiosError?.response?.data;

    // Парсим вложенную структуру ответа
    const data = responseData && typeof responseData === 'object' && 'data' in responseData
      ? (responseData as any).data
      : responseData;

    const attemptsLeft = data?.context?.attemptsLeft;
    const message = data?.message;

    // Попытки исчерпаны: attemptsLeft === 0, HTTP 429 или ключевое слово в сообщении
    if (
      (attemptsLeft !== undefined && attemptsLeft <= 0)
      || status === 429
      || (typeof message === 'string' && message.toLowerCase().includes('исчерпал'))
    ) {
      view.value = 'exhausted';
      return;
    }

    if (attemptsLeft !== undefined) {
      errorMessage.value = `Код введен неверно, осталось ${attemptsLeft} ${getAttemptsWord(attemptsLeft)}`;
    } else if (message) {
      errorMessage.value = message;
    } else {
      errorMessage.value = 'Код введен неверно';
    }
    pinCodeRef.value?.clearPinCode();
  } finally {
    loading.value = false;
  }
};

const resendCode = async () => {
  if (resendCooldown.value > 0 && view.value === 'input') return;

  try {
    let id = currentRegistrationId.value;

    // Код просрочен — нужно заново вызвать start + step2
    if (view.value === 'expired') {
      const startResult = await AuthService.registerStart(props.email);
      id = startResult.id;
      currentRegistrationId.value = id;
    }

    const result = await AuthService.registerStep2(id, props.password);
    if (result.expiredAt) {
      currentExpiredAt.value = result.expiredAt;
    }
    view.value = 'input';
    errorMessage.value = '';
    pinCodeRef.value?.clearPinCode();
    startExpiryTimer();
    startResendCooldown();
  } catch {
    errorMessage.value = 'Не удалось отправить код. Попробуйте позже.';
  }
};

const getAttemptsWord = (n: number): string => {
  if (n === 1) return 'попытка';
  if (n >= 2 && n <= 4) return 'попытки';
  return 'попыток';
};

onMounted(() => {
  startExpiryTimer();
  startResendCooldown();
});

onUnmounted(() => {
  if (expiryInterval) clearInterval(expiryInterval);
  if (resendInterval) clearInterval(resendInterval);
});
</script>
